import { useCallback, useEffect, useRef, useState } from 'react';
import { useToggle } from '../useToggle';

/**
 * Custom hook to manage a stepped animation for table rows.
 * This hook provides functionalities to animate table rows in steps, useful for creating a scrolling effect.
 *
 * @param {number} duration - The duration of each animation step in milliseconds.
 * @param {boolean} active - Flag to indicate if the animation should be active.
 * @param {boolean} isLoading - Flag to indicate if the data is still loading, which pauses the animation.
 * @returns {Object} An object containing a ref to the table element, a flag indicating if the animation has ended, and the current step index.
 *
 * @example
 * // Usage example:
 * const { ref, endAnimation, currentStep } = useSteppedAnimation(1000, true, false);
 */
const useSteppedAnimation = (
  duration: number,
  active: boolean,
  isLoading: boolean,
) => {
  // Ref to the table element
  const ref = useRef<HTMLTableElement>(null);

  // State to manage the end of the animation
  const [endAnimation, toggleEndAnimation] = useToggle(true);

  // State to track the current step of the animation
  const [currentStep, setCurrentStep] = useState(0);

  /**
   * Updates the position of each row in the table based on the current step.
   *
   * @param {NodeListOf<Element>} rows - The list of row elements to animate.
   * @param {number} totalRows - The total number of rows in the table.
   * @param {number} stepHeight - The height of each step in pixels.
   */
  const updateRowPosition = useCallback(
    (rows: NodeListOf<Element>, totalRows: number, stepHeight: number) => {
      if (isLoading) {
        return;
      }

      if (currentStep >= totalRows) {
        setCurrentStep(9);

        rows.forEach(row => {
          const element = row as HTMLElement;
          element.style.transition = 'none';
          element.style.transform = `translateY(-${stepHeight * (totalRows - 1 - 9)}px)`;
        });

        if (!endAnimation) {
          toggleEndAnimation();
        }

        return;
      }

      rows.forEach(row => {
        const element = row as HTMLElement;
        element.style.transition =
          'transform 1s cubic-bezier(0.47, 1.64, 0.41, 0.8)';
        element.style.transform = `translateY(-${stepHeight * (totalRows - 1 - currentStep)}px)`;
      });
      if (endAnimation) {
        toggleEndAnimation();
      }

      setCurrentStep(prev => prev + 1);
    },
    [currentStep, endAnimation, toggleEndAnimation, isLoading],
  );

  /**
   * Handles the animation steps by setting an interval to update row positions.
   * Clears the interval when the component is unmounted or when the animation is stopped.
   */
  const stepAnimationHandler = useCallback(() => {
    if (!active || isLoading) return;

    const rows = ref.current?.querySelectorAll('.row') || [];
    const totalRows = rows.length;
    const stepHeight = 51;

    if (totalRows > 0) {
      const intervalId = setInterval(
        () =>
          updateRowPosition(rows as NodeListOf<Element>, totalRows, stepHeight),
        duration,
      );

      return () => clearInterval(intervalId);
    }
  }, [active, duration, updateRowPosition, isLoading]);

  // Effect to start the animation when the 'active' flag is true
  useEffect(() => {
    if (active) {
      const cleanup = stepAnimationHandler();
      return () => cleanup && cleanup();
    }
  }, [stepAnimationHandler, active]);

  return { ref, endAnimation, currentStep };
};

export default useSteppedAnimation;
